import { HttpClient } from '@angular/common/http'
import { inject, Injectable, OnDestroy } from '@angular/core'
import { NavbarService } from '@app/v2/shared/components/navbar/navbar.service'
import { environment } from '@environments/environment'
import { BehaviorSubject, interval, Observable, Subject } from 'rxjs'
import { switchMap, takeUntil, takeWhile, tap } from 'rxjs/operators'
import { BulkOperation, ProgressData } from '../models/bulk-operation.interface'

@Injectable({
  providedIn: 'root'
})
export class BulkProgressService implements OnDestroy {
  private _operations$ = new BehaviorSubject<BulkOperation[]>([])
  public operations$ = this._operations$.asObservable()
  private _destroy$ = new Subject()
  private _pollingSubscriptions = new Map<string, Subject<void>>()

  // Emit when an operation completes (for triggering data refresh + clear selections)
  private _operationCompleted$ = new Subject<{ batchId: string; operation: string; status: 'completed' | 'failed' }>()
  public operationCompleted$ = this._operationCompleted$.asObservable()

  private readonly _navbarService = inject(NavbarService)

  constructor(private http: HttpClient) {}

  ngOnDestroy(): void {
    this._destroy$.next(null)
    this._destroy$.complete()
    // Stop all polling
    this._pollingSubscriptions.forEach((sub) => {
      sub.next()
      sub.complete()
    })
    this._pollingSubscriptions.clear()
  }

  /**
   * Add a new bulk operation to track
   */
  addOperation(batchId: string, operation: string, total: number, eventName: string): void {
    console.log('✅ BulkProgressService.addOperation called', { batchId, operation, total, eventName })
    const operations = this._operations$.value
    const newOperation: BulkOperation = {
      batchId,
      operation: operation as any,
      total,
      processed: 0,
      failed: 0,
      status: 'processing',
      eventName,
      hidden: false,
      startedAt: new Date()
    }
    this._operations$.next([...operations, newOperation])
    console.log('📋 Operations array now has', this._operations$.value.length, 'operations')
  }

  /**
   * Update an existing operation with progress data
   */
  updateOperation(batchId: string, progress: ProgressData): void {
    const operations = this._operations$.value
    const index = operations.findIndex((op) => op.batchId === batchId)
    if (index !== -1) {
      const previousStatus = operations[index].status
      console.log('📝 Updating operation:', {
        batchId,
        previousStatus,
        newStatus: progress.status,
        processed: progress.processed,
        total: progress.total
      })

      operations[index] = {
        ...operations[index],
        processed: progress.processed,
        total: progress.total,
        failed: progress.failed || 0,
        status: progress.status as any
      }
      this._operations$.next([...operations])

      // Emit completion event if status changed to completed or failed
      if (previousStatus === 'processing' && (progress.status === 'completed' || progress.status === 'failed')) {
        console.log('🎉 EMITTING COMPLETION EVENT:', {
          batchId,
          operation: operations[index].operation,
          status: progress.status
        })
        this._operationCompleted$.next({
          batchId,
          operation: operations[index].operation,
          status: progress.status as 'completed' | 'failed'
        })
        // Always refresh wallet balance when any bulk job completes successfully
        if (progress.status === 'completed') {
          this._navbarService.refreshBalance()
        }
      } else {
        console.log('⏭️ Not emitting completion (status unchanged or not processing→complete):', {
          previousStatus,
          newStatus: progress.status
        })
      }
    } else {
      console.warn('⚠️ Operation not found for update:', batchId)
    }
  }

  /**
   * Update the batch ID of an operation (used when temp ID is replaced with real batch_id)
   */
  updateOperationId(oldBatchId: string, newBatchId: string): void {
    const operations = this._operations$.value
    const index = operations.findIndex((op) => op.batchId === oldBatchId)
    if (index !== -1) {
      operations[index] = {
        ...operations[index],
        batchId: newBatchId
      }
      this._operations$.next([...operations])
      console.log('🔄 Updated operation ID from', oldBatchId, 'to', newBatchId)
    }
  }

  /**
   * Remove an operation from tracking
   */
  removeOperation(batchId: string): void {
    const operations = this._operations$.value
    this._operations$.next(operations.filter((op) => op.batchId !== batchId))

    // Stop polling for this operation
    const pollingSub = this._pollingSubscriptions.get(batchId)
    if (pollingSub) {
      pollingSub.next()
      pollingSub.complete()
      this._pollingSubscriptions.delete(batchId)
    }
  }

  /**
   * Dismiss an operation (alias for removeOperation)
   */
  dismissOperation(batchId: string): void {
    this.removeOperation(batchId)
  }

  /**
   * Hide an operation (minimize to side button)
   */
  hideOperation(batchId: string): void {
    const operations = this._operations$.value
    const index = operations.findIndex((op) => op.batchId === batchId)
    if (index !== -1) {
      operations[index] = {
        ...operations[index],
        hidden: true
      }
      this._operations$.next([...operations])
    }
  }

  /**
   * Expand a hidden operation
   */
  expandOperation(batchId: string): void {
    const operations = this._operations$.value
    const index = operations.findIndex((op) => op.batchId === batchId)
    if (index !== -1) {
      operations[index] = {
        ...operations[index],
        hidden: false
      }
      this._operations$.next([...operations])
    }
  }

  /**
   * Poll progress for a batch operation
   */
  pollProgress(batchId: string): Observable<ProgressData> {
    return this.http.get<ProgressData>(`${environment.registrationsApi}bulk-progress/${batchId}`)
  }

  /**
   * Start polling for progress updates
   */
  startPolling(batchId: string): void {
    console.log('🔄 Starting polling for batch:', batchId)

    // Create a new destroy subject for this polling subscription
    const pollDestroy$ = new Subject<void>()
    this._pollingSubscriptions.set(batchId, pollDestroy$)

    // Poll immediately first, then every 5 seconds
    this.pollProgress(batchId).subscribe({
      next: (progress) => {
        console.log('📊 Initial poll result:', batchId, progress)
        this.updateOperation(batchId, progress)
      },
      error: (err) => {
        console.error('❌ Initial poll error:', err)
      }
    })

    interval(5000)
      .pipe(
        switchMap(() => {
          console.log('🔄 Polling batch:', batchId)
          return this.pollProgress(batchId)
        }),
        tap((progress) => {
          console.log('📊 Poll result:', batchId, progress)
          this.updateOperation(batchId, progress)
        }),
        takeWhile((progress) => progress.status === 'processing' && !(progress.total > 0 && progress.processed >= progress.total), true),
        takeUntil(pollDestroy$),
        takeUntil(this._destroy$)
      )
      .subscribe({
        complete: () => {
          console.log('✅ Polling completed for batch:', batchId)
          // Clean up polling subscription
          const sub = this._pollingSubscriptions.get(batchId)
          if (sub) {
            sub.complete()
            this._pollingSubscriptions.delete(batchId)
          }
        },
        error: (err) => {
          console.error('❌ Error polling progress:', err)
          // On error, mark operation as failed
          this.updateOperation(batchId, {
            processed: 0,
            total: 0,
            status: 'failed'
          })
          // Clean up
          const sub = this._pollingSubscriptions.get(batchId)
          if (sub) {
            sub.complete()
            this._pollingSubscriptions.delete(batchId)
          }
        }
      })
  }
}
